import { Box, Group, Stack, Text, Title } from "@mantine/core";
import { IconBriefcase, IconCoin, IconShieldCheck, IconTrendingUp } from "@tabler/icons-react";
import type { ReactNode } from "react";
import {
  AUTH_FORM_COLUMN,
  AUTH_FORM_WIDTH,
  AUTH_GRID,
  AUTH_HERO,
  AUTH_HERO_LOGO_HEIGHT,
  AUTH_MOBILE_LOGO_HEIGHT,
  AUTH_COLUMN_ALIGNMENT,
  AUTH_CENTERED_GRID_ROWS,
  GLASS_CARD,
  useAuthDocumentBackdrop,
} from "./authLayout";
import "./authAnimations.css";
import { BrandLogo } from "./BrandLogo";
import { axioTokens } from "../theme";

const HIGHLIGHTS = [
  { icon: IconCoin, text: "Earn up to 75% of every placement fee" },
  { icon: IconBriefcase, text: "Voice BPO, Chat, IT Support & more" },
  { icon: IconTrendingUp, text: "Track candidates through every hiring stage" },
];

interface AuthShellProps {
  title: string;
  subtitle: string;
  children: ReactNode;
  footer?: ReactNode;
}

export function AuthShell({ title, subtitle, children, footer }: AuthShellProps) {
  useAuthDocumentBackdrop(axioTokens.authHeroGradient);

  return (
    <Box className="authGrid" style={{ ...AUTH_GRID, background: axioTokens.authHeroGradient }}>
      {/* Brand rail — animated aurora with floating particles */}
      <Box
        component="aside"
        aria-label="About AxioHire"
        visibleFrom="md"
        className="aurora authHeroColumn"
        style={{
          ...AUTH_HERO,
          display: "flex",
          flexDirection: "column",
          justifyContent: AUTH_COLUMN_ALIGNMENT.center,
          alignItems: AUTH_COLUMN_ALIGNMENT.top,
        }}
      >
        {/* Decorative orbs */}
        <div className="heroOrb1" aria-hidden />
        <div className="heroOrb2" aria-hidden />

        <Stack gap="var(--axio-unit-36)" maw="var(--axio-unit-400)" align={AUTH_COLUMN_ALIGNMENT.top} style={{ position: "relative", zIndex: "var(--axio-number-one)" }}>
          <BrandLogo variant="full" scheme="dark" height={AUTH_HERO_LOGO_HEIGHT} to={null} />

          <Text c={axioTokens.whiteAlpha68} size="sm" lh={axioTokens.lineHeight.airy} maw="var(--axio-unit-340)">
            India&apos;s marketplace for high-volume, pay-on-success hiring.
          </Text>

          <Stack gap="lg" mt="var(--axio-unit-8)">
            {HIGHLIGHTS.map(({ icon: Icon, text }) => (
              <Group key={text} gap="md" wrap="nowrap" align="flex-start">
                <Box
                  style={{
                    width: "var(--axio-unit-40)",
                    height: "var(--axio-unit-40)",
                    borderRadius: "var(--axio-radius)",
                    background: axioTokens.violetAlpha18,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: "var(--axio-number-zero)",
                    backdropFilter: `blur(${axioTokens.dimension.blurMd})`,
                  }}
                >
                  <Icon size="var(--axio-unit-20)" color={axioTokens.violet2} stroke="var(--axio-stroke-width-subtle)" />
                </Box>
                <Text c={axioTokens.whiteAlpha90} size="sm" lh={axioTokens.lineHeight.airy} pt="var(--axio-unit-2)">
                  {text}
                </Text>
              </Group>
            ))}
          </Stack>

        </Stack>
      </Box>

      {/* Form column — three rows so the card centers on the hero regardless
          of how tall the footer block below it is. */}
      <Box
        component="main"
        className="formColumnBg authScrollColumn"
        style={{
          ...AUTH_FORM_COLUMN,
          display: "grid",
          gridTemplateRows: AUTH_CENTERED_GRID_ROWS,
          justifyItems: "center",
          alignItems: "stretch",
        }}
      >
        {/* Row 1 — spacer on desktop, brand lockup on mobile. Rows are placed
            explicitly because this element is display:none from md up and so
            drops out of grid auto-placement entirely. */}
        <Box
          hiddenFrom="md"
          style={{
            gridRow: 1,
            alignSelf: "end",
            display: "flex",
            justifyContent: "center",
            paddingBottom: "var(--axio-unit-32)",
          }}
        >
          <BrandLogo variant="full" scheme="light" height={AUTH_MOBILE_LOGO_HEIGHT} to={null} />
        </Box>

        {/* Row 2 — the card, vertically centered */}
        <Box w="100%" maw={AUTH_FORM_WIDTH} style={{ gridRow: 2, alignSelf: "center" }}>
          <Box
            className="staggerEnter"
            data-testid="auth-form-card"
            style={{ ...GLASS_CARD, animationDelay: axioTokens.duration.progressDelay }}
          >
            <Stack gap="var(--axio-unit-6)" mb="xl" className="staggerEnter" style={{ animationDelay: axioTokens.duration.instant }}>
              <Title order={2} fw="var(--axio-font-weight-extra-bold)" style={{ fontFamily: axioTokens.fontHead, letterSpacing: axioTokens.letterSpacing.tighter }}>
                {title}
              </Title>
              <Text c="dimmed" size="md" lh="var(--axio-line-height-relaxed)">
                {subtitle}
              </Text>
            </Stack>
            {children}
            {footer && (
              <Text
                component="div"
                ta="center"
                mt="xl"
                size="sm"
                c="dimmed"
                lh="var(--axio-line-height-relaxed)"
                className="staggerEnter"
                style={{ animationDelay: axioTokens.duration.input }}
              >
                {footer}
              </Text>
            )}
            <Group
              gap="var(--axio-unit-6)"
              justify="center"
              mt="lg"
              className="staggerEnter"
              style={{ animationDelay: axioTokens.duration.medium }}
            >
              <IconShieldCheck size="var(--axio-unit-14)" color={axioTokens.muted} stroke="var(--axio-stroke-width-subtle)" />
              <Text size="xs" c="dimmed" fw="var(--axio-font-weight-medium)">
                Secured by AxioHire
              </Text>
            </Group>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
